import { Button, Card, Checkbox, Col, Divider, Flex, Form, Input, Row, Segmented, Table, Tooltip } from 'antd';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// components
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import CompoundElement from 'components/compoundElement/compoundElement';
import DisabledElement from 'components/disabledElement/disabledElement';

// services
import {
	onCreateLoyaltyProgram,
	onGetLoyaltyProgramStatus,
	onGetTreasuryShares,
	onSimulateDistributionLoyaltyProgramDates,
} from '../services/loyaltyProgramService';
import { CreateLoyalityProgramPayload, SimulateDistributionPayload, SimulateDistributionResponse } from '../models/loyaltyProgram';
import { VerificationPayload } from '../../commonModels';

// models
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { ActionMaskedShareholderResponse } from '../../commonModels';
import StatusComponent from 'components/status/status';
import Icon from 'utils/icon';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import Riyal from 'components/riyal/riyal';
import DatePickerElement from 'components/datePicker/datePicker';
import dayjs from 'dayjs';
import SelectElement from 'components/selectElement/selectElement';
import VerificationModal from '../../verificationModal';
import { onCheckMaskedNationalId } from '../../commonServices';
import { isRTL } from 'core/services/language';
import FormElement from 'components/formElement/formElement';
import SwitchElement from 'components/switch/switch';
import SharesStepper from 'components/sharesStepper/sharesStepper';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import { checkPhoneValidation } from 'utils/validators/phone';
import InfoTooltip from 'components/info/info';

type FormFields = {
	message: string;
	notice_type: string;
	national_id: string;
	shareholder_name: string;
	shareholder_phone: string;
	shareholder_country_code: string;
};

interface LoyaltyProgramCreatePageProps {}

const LoyaltyProgramCreatePage: FunctionComponent<LoyaltyProgramCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [loyaltyProgramForm] = Form.useForm();
	const [newShareholderForm] = Form.useForm();

	const loyaltyProgramTypeWatch = Form.useWatch('type', loyaltyProgramForm);
	const numberOfSharesWatch = Form.useWatch('number_of_shares', loyaltyProgramForm);
	const numberOfIntervalsWatch = Form.useWatch('number_of_intervals', loyaltyProgramForm);
	const intervalPeriodWatch = Form.useWatch('interval_period', loyaltyProgramForm);
	const intervalAmountWatch = Form.useWatch('interval_amount', loyaltyProgramForm);
	const eligibilityDateWatch = Form.useWatch('eligibility_date', loyaltyProgramForm);
	const priceWatch = Form.useWatch('price', loyaltyProgramForm);
	const acceptanceDaysWatch = Form.useWatch('acceptance_days', loyaltyProgramForm);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [maxAvailableShares, setMaxAvailableShares] = useState<number>(250);

	const [nationalId, setNationalId] = useState<string>('');
	const [isChecking, setIsChecking] = useState<boolean>(false);
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [shareholder, setShareholder] = useState<ActionMaskedShareholderResponse['shareholder']>();

	const [isRestrictedByDate, setIsRestrictedByDate] = useState<boolean>(false);
	const [isPeriodic, setIsPeriodic] = useState<boolean>(false);
	const [isSalaryDeduction, setIsSalaryDeduction] = useState<boolean>(false);
	const [isCurrentEmploymentCondition, setIsCurrentEmploymentCondition] = useState<boolean>(false);

	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	const [acknowledge, setAcknowledge] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const [installments, setInstallments] = useState<SimulateDistributionResponse['installments']>();
	const [isSimulating, setIsSimulating] = useState<boolean>(false);

	const isGrant = loyaltyProgramTypeWatch === 'grant';
	const isOption = loyaltyProgramTypeWatch === 'option';

	const loyaltyProgramTypeOptions = [
		{
			value: 'grant',
			label: t('ACTIONS.GRANT'),
		},
		{
			value: 'option',
			label: t('ACTIONS.OPTION'),
		},
	];

	const intervalRows = useMemo(
		() =>
			(installments ?? []).map((installment) => ({
				key: installment.installment_number,
				order: installment.installment_number,
				grantDate: installment.distribution_date,
				amount: installment.number_of_shares,
			})),
		[installments],
	);

	const distributedTotal = useMemo(() => intervalRows.reduce((sum, r) => sum + r.amount, 0), [intervalRows]);

	// Manually adjust a single installment's shares; kept in `installments` state so it can feed onCreateLoyaltyProgram payload
	const updateInstallmentAmount = (installmentNumber: number, delta: number) => {
		setInstallments((prev) =>
			prev?.map((installment) =>
				installment.installment_number === installmentNumber
					? { ...installment, number_of_shares: Math.max(0, installment.number_of_shares + delta) }
					: installment,
			),
		);
	};

	useEffect(() => {
		// getLoyaltyProgramStatus();
		getTreasuryShares();
	}, []);

	// Re-validate the eligibility/start date against the new minimum when acceptance days change
	useEffect(() => {
		if (loyaltyProgramForm.getFieldValue('eligibility_date')) {
			loyaltyProgramForm.validateFields(['eligibility_date']);
		}
	}, [acceptanceDaysWatch]);

	useEffect(() => {
		if (!isPeriodic) {
			setInstallments(undefined);
			return;
		}

		const intervalMonths = loyaltyProgramForm.getFieldValue('interval_months');
		const intervalAmount = isGrant ? numberOfIntervalsWatch : intervalAmountWatch;
		const numberOfShares = Number(numberOfSharesWatch);
		const startDate = eligibilityDateWatch;

		if (!intervalMonths || !Number(intervalAmount) || !numberOfShares || !startDate || nationalId.length !== 10) {
			setInstallments(undefined);
			return;
		}

		const timeout = setTimeout(() => simulateDistribution(), 400);
		return () => clearTimeout(timeout);
	}, [
		isPeriodic,
		isGrant,
		intervalPeriodWatch,
		intervalAmountWatch,
		numberOfIntervalsWatch,
		numberOfSharesWatch,
		eligibilityDateWatch,
		priceWatch,
		acceptanceDaysWatch,
		nationalId,
	]);

	const simulateDistribution = async () => {
		try {
			setIsSimulating(true);

			const intervalAmount = isGrant ? numberOfIntervalsWatch : intervalAmountWatch;
			const startDate = eligibilityDateWatch;

			const payload: SimulateDistributionPayload = {
				incentive_type: isGrant ? 'Grant' : 'Option',
				restriction_type: 'Periodic',
				eligibility_date: dayjs(startDate).format('YYYY-MM-DD'),
				beneficiary_national_id: nationalId,
				number_of_shares: Number(numberOfSharesWatch),
				price_per_share: Number(priceWatch) || 0,
				interval_months: loyaltyProgramForm.getFieldValue('interval_months'),
				interval_amount: Number(intervalAmount),
				acceptance_days: Number(acceptanceDaysWatch) || 0,
			};

			const response = await onSimulateDistributionLoyaltyProgramDates(payload);
			setInstallments(response?.installments);
		} catch (error) {
			setInstallments(undefined);
		} finally {
			setIsSimulating(false);
		}
	};

	const getTreasuryShares = async () => {
		try {
			const response = await onGetTreasuryShares();
			if (response.free_treasury_shares === 0) {
				NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.NO_SHARES_AVAILABLE'));
				navigate('/dashboard/actions/loyalty-programs');
				return;
			}
			setMaxAvailableShares(response.free_treasury_shares);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/loyalty-programs');
		} finally {
			setIsLoading(false);
		}
	};

	const getLoyaltyProgramStatus = async () => {
		try {
			const status = await onGetLoyaltyProgramStatus();
			if (status.is_free_plan || status.has_active_general_suspend_action) navigate('/dashboard/actions/loyalty-programs');
		} catch (error) {}
	};

	const onCheckShareholder = async (value: string) => {
		if (value.length === 10) {
			try {
				setIsChecking(true);
				const response = await onCheckMaskedNationalId(value);
				if (response.success && response.data.shareholder_exists) setShareholder(response.data.shareholder);
				setIsChecking(false);
				setIsChecked(true);
			} catch (error: any) {
				setIsChecking(false);
			}
		} else {
			setIsChecking(false);
			setShareholder(undefined);
		}
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		newShareholderForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(newShareholderForm.getFieldValue('phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const onChangeType = (value: string) => {
		loyaltyProgramForm.setFieldsValue({ type: value });
		setIsRestrictedByDate(false);
		setIsPeriodic(false);
		setIsSalaryDeduction(false);
		setIsCurrentEmploymentCondition(false);
	};

	const resetShareholderData = (value: string) => {
		loyaltyProgramForm.resetFields([
			['newShareholder', 'name'],
			['newShareholder', 'country_id'],
			['newShareholder', 'phone_country_code'],
			['newShareholder', 'phone_number'],
		]);
		setNationalId(value);
		setIsChecked(false);
		setShareholder(undefined);
	};

	const validateSharesCountInput = (e: any) => {
		if (Number(e.target.value) > maxAvailableShares) {
			e.target.value = maxAvailableShares;
			loyaltyProgramForm.setFieldsValue({ number_of_shares: maxAvailableShares });
			return;
		}
		loyaltyProgramForm.setFieldsValue({ number_of_shares: Number(e.target.value) });
	};

	const onFinish = async () => {
		setVerificationModalOpen(true);
	};

	const onSubmit = async (verification: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const restrictionType = isPeriodic ? 'Periodic' : isRestrictedByDate ? 'RestrictedByDate' : 'None';
			const intervalAmount = isGrant ? numberOfIntervalsWatch : intervalAmountWatch;

			const payload: CreateLoyalityProgramPayload = {
				incentive_type: isGrant ? 'Grant' : 'Option',
				restriction_type: restrictionType,
				beneficiary_national_id: nationalId,
				number_of_shares: Number(numberOfSharesWatch),
				verified_using: verification.method,
				...(verification.method === 'otp' ? { otp: verification.key } : { password: verification.key }),
			};

			if (restrictionType !== 'None') {
				payload.eligibility_date = dayjs(eligibilityDateWatch).format('YYYY-MM-DD');
				payload.acceptance_days = Number(acceptanceDaysWatch) || 0;
			}

			if (restrictionType === 'Periodic') {
				payload.interval_months = Number(loyaltyProgramForm.getFieldValue('interval_months')) || 0;
				payload.interval_amount = Number(intervalAmount) || 0;
				payload.installments = (installments ?? []).map(({ installment_number, number_of_shares }) => ({ installment_number, number_of_shares }));
				payload.current_employment_condition = isCurrentEmploymentCondition;
			}

			if (isOption) {
				payload.price_per_share = Number(priceWatch) || 0;
				payload.salary_deduction = isSalaryDeduction;
			}

			await onCreateLoyaltyProgram(payload);

			NotificationService.success(
				t('COMMON.SUCCESS'),
				t('ACTIONS.LOYALTY_PROGRAM_CREATED', { type: isGrant ? t('ACTIONS.GRANT') : t('ACTIONS.OPTION') }),
			);
			setVerificationModalOpen(false);
			navigate('/dashboard/actions/loyalty-programs');
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		} finally {
			setIsSubmitting(false);
		}
	};

	const grantTableColumns = [
		{ title: t('ACTIONS.ORDER'), dataIndex: 'order', key: 'order', width: 80 },
		{ title: t('ACTIONS.GRANT_DATE'), dataIndex: 'grantDate', key: 'grantDate' },
		{
			title: t('ACTIONS.SHARES_COUNT'),
			dataIndex: 'amount',
			key: 'amount',
			render: (_: any, record: any) => (
				<Flex gap={8} align="center" justify="center">
					<Button
						type="primary"
						className="h-max"
						style={{ padding: 5 }}
						onClick={() => updateInstallmentAmount(record.order, -1)}
						disabled={record.amount === 0}>
						<Icon name="minus" color="#fff" width={12} height={12} />
					</Button>
					<span style={{ minWidth: 30, textAlign: 'center' }}>{record.amount}</span>
					<Button
						type="primary"
						className="h-max"
						style={{ padding: 5 }}
						disabled={distributedTotal == numberOfSharesWatch}
						onClick={() => updateInstallmentAmount(record.order, 1)}>
						<Icon name="plus" color="#fff" width={12} height={12} />
					</Button>
				</Flex>
			),
		},
	];

	const optionTableColumns = [
		...grantTableColumns,
		{
			title: 'Total Cost',
			dataIndex: 'totalCost',
			key: 'totalCost',
			render: (_: any, record: any) => `${(record.amount * (loyaltyProgramForm.getFieldValue('price') || 0)).toFixed(2)}`,
		},
	];

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<Form form={loyaltyProgramForm} onFinish={onFinish} initialValues={{ number_of_shares: 0, type: 'grant' }}>
						<Card>
							<ComponentHeader title={t('ACTIONS.LOYALTY_PROGRAMS')} subtitle={t('ACTIONS.LOYALTY_PROGRAMS_CREATE_DESC')} />

							<Segmented
								className="custom white"
								options={loyaltyProgramTypeOptions}
								block
								size="large"
								style={{ marginBottom: 30 }}
								onChange={onChangeType}
							/>
							<Form.Item name="type" noStyle />

							<Flex gap={20} align="center" className="mb-3">
								<p className="mb-0 fw-medium">{loyaltyProgramTypeWatch == 'grant' ? t('ACTIONS.GRANT_DESC') : t('ACTIONS.OPTION_DESC')}</p>

								<StatusComponent
									color="green"
									bordered
									style={{ display: 'inline-block' }}
									text={t('ACTIONS.AVAILABLE_TREASURY_SHARES', { num: maxAvailableShares })}
								/>
							</Flex>

							<Divider />

							{/* Beneficiary */}
							<Row gutter={[16, 16]}>
								<Col xs={24} md={12}>
									<CompoundElement
										buttonText={t('COMMON.CHECK')}
										onSubmit={onCheckShareholder}
										label={t('COMMON.NATIONAL_ID')}
										isLoading={isChecking}
										onChange={resetShareholderData}
									/>
								</Col>
								<Col xs={24} md={12}>
									{isChecked && shareholder && (
										<DisabledElement
											value={isRTL() ? shareholder?.translated_name?.ar : (shareholder?.translated_name?.en ?? shareholder?.translated_name?.ar)}
											label={t('COMMON.NAME')}
										/>
									)}
								</Col>

								{isChecked && !shareholder && (
									<>
										<Col xs={24} md={8}>
											<FormElement name="name" type="text" label={t('ACTIONS.FULL_NAME')} />
										</Col>
										<Col xs={24} md={8}>
											<FormElement name="national_id" type="text" label={t('COMMON.NATIONAL_ID')} />
										</Col>
										<Col xs={24} md={8}>
											<Form.Item
												name="phone_number"
												style={{ maxWidth: '100%' }}
												rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
												<PhoneInputComponent onChange={onChangePhone} fullWidth />
											</Form.Item>
											<Form.Item name="phone_country_code" hidden>
												<Input hidden />
											</Form.Item>
										</Col>
									</>
								)}
							</Row>

							<Divider />

							{/* Main fields */}
							<Row gutter={[24, 16]} align="top">
								{/* Left column: type-specific fields */}
								<Col xs={24} md={16}>
									<Row gutter={[16, 16]}>
										{/* Shares stepper */}
										<Col flex="none">
											<SharesStepper
												name="number_of_shares"
												label={t('ACTIONS.GRANTED_SHARES')}
												value={Number(numberOfSharesWatch)}
												max={maxAvailableShares}
												rules={[
													{
														validator: (_, value) =>
															Number(value) > 0 ? Promise.resolve() : Promise.reject(new Error(t('COMMON.NUMBER_OF_SHARES_GREATER_THAN_ZERO'))),
													},
												]}
												onDecrement={() => loyaltyProgramForm.setFieldsValue({ number_of_shares: Number(numberOfSharesWatch) - 1 })}
												onIncrement={() => loyaltyProgramForm.setFieldsValue({ number_of_shares: Number(numberOfSharesWatch) + 1 })}
												onInput={validateSharesCountInput}
											/>
										</Col>

										{/* Price – Option only */}
										{isOption && (
											<Col xs={24} sm={8}>
												<FormNumberElement
													name="price"
													label={t('ACTIONS.OPTION_VIEW')}
													addonAfter={
														<Flex align="center" gap={5}>
															<Riyal />/ {t('ACTIONS.SHARE')}
														</Flex>
													}
												/>
											</Col>
										)}

										{(isOption || isPeriodic || isRestrictedByDate) && (
											<Col span={24} lg={6}>
												<SelectElement
													label={t('ACTIONS.ACCEPTANCE_DAYS')}
													name="acceptance_days"
													rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
													options={[
														{ label: t('ACTIONS.3_DAYS'), value: '1' },
														{ label: t('ACTIONS.7_DAYS'), value: '7' },
														{ label: t('ACTIONS.14_DAYS'), value: '14' },
														{ label: t('ACTIONS.21_DAYS'), value: '21' },
													]}
												/>
											</Col>
										)}

										{((!isPeriodic && isRestrictedByDate) || isPeriodic) && (
											<Col span={24} lg={6}>
												<DatePickerElement
													label={isPeriodic ? t('ACTIONS.LOYALITY_START_DATE') : t('ACTIONS.ELIGIBILITY_DATE')}
													name="eligibility_date"
													minDate={dayjs()
														.add(Number(acceptanceDaysWatch) + 1 || 0, 'day')
														.startOf('day')}
													rules={[
														{ required: true, message: t('COMMON.FIELD_REQUIRED') },
														{
															validator: (_, value) => {
																if (!value) return Promise.resolve();

																const minDate = dayjs()
																	.add(Number(acceptanceDaysWatch) + 1 || 0, 'day')
																	.startOf('day');
																return value.startOf('day').isBefore(minDate)
																	? Promise.reject(
																			new Error(
																				t('ACTIONS.ELIGIBILITY_DATE_MIN', {
																					days: Number(acceptanceDaysWatch) + 1 || 0,
																				}),
																			),
																		)
																	: Promise.resolve();
															},
														},
													]}
												/>
											</Col>
										)}
									</Row>

									{isPeriodic && (
										<Row gutter={[16, 16]} className="mt-2">
											<Col span={24} lg={8}>
												<SelectElement
													label={t('ACTIONS.INTERVAL_PERIOD')}
													name="interval_months"
													rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
													options={[
														{ label: t('ACTIONS.ONE_MONTH'), value: '1' },
														{ label: t('ACTIONS.THREE_MONTHS'), value: '3' },
														{ label: t('ACTIONS.SIX_MONTHS'), value: '6' },
														{ label: t('ACTIONS.NINE_MONTHS'), value: '9' },
														{ label: t('ACTIONS.TWELVE_MONTHS'), value: '12' },
													]}
												/>
											</Col>
											{isGrant ? (
												<Col span={24} lg={8}>
													<FormElement
														name="number_of_intervals"
														label={t('ACTIONS.NUMBER_OF_INTERVALS')}
														rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
														type="number"
													/>
												</Col>
											) : (
												<Col xs={24} lg={8}>
													<FormNumberElement name="interval_amount" label={t('ACTIONS.INTERVAL_AMOUNT')} />
												</Col>
											)}
										</Row>
									)}
								</Col>

								{/* Right column: toggles */}
								<Col xs={24} md={8}>
									<Flex vertical gap={16}>
										{isOption && (
											<SwitchElement
												controlled
												checked={isSalaryDeduction}
												onChange={setIsSalaryDeduction}
												description={t('ACTIONS.COMPANY_DEDUCTION')}
												label={
													<Flex align="center" gap={5}>
														{t('ACTIONS.SALARY_DEDUCTION_PAYMENT')}
														<InfoTooltip title={t('ACTIONS.SALARY_DEDUCTION_PAYMENT_INFO')} />
													</Flex>
												}
											/>
										)}

										<SwitchElement
											controlled
											checked={isRestrictedByDate}
											onChange={(v) => {
												setIsRestrictedByDate(v);
												if (v) setIsPeriodic(false);
											}}
											label={t('ACTIONS.RESTRICTED_BY_DATE')}
											description={t('ACTIONS.RESTRICTED_BY_DATE_DESC')}
											disabled={isPeriodic && isOption}
										/>

										<SwitchElement
											controlled
											checked={isPeriodic}
											onChange={(v) => {
												setIsPeriodic(v);
												if (v) setIsRestrictedByDate(false);
											}}
											label={t('ACTIONS.LOYALTY_PROGRAM_PERIODIC')}
											description={t('ACTIONS.PERIODIC_DESC')}
										/>

										{isPeriodic && (
											<SwitchElement
												controlled
												checked={isCurrentEmploymentCondition}
												onChange={setIsCurrentEmploymentCondition}
												label={t('ACTIONS.CURRENT_EMPLOYMENT_CONDITION')}
												description={t('ACTIONS.CURRENT_EMPLOYMENT_CONDITION_DESC')}
											/>
										)}
									</Flex>
								</Col>
							</Row>

							{/* Periodic distribution table */}
							{isPeriodic && (isSimulating || intervalRows.length > 0) && (
								<div style={{ width: '600px', margin: 'auto' }}>
									<Table
										dataSource={intervalRows}
										columns={isOption ? optionTableColumns : grantTableColumns}
										pagination={false}
										size="small"
										loading={isSimulating}
										className="bordered mt-4 mb-2"
										style={{ maxWidth: '100%', margin: 'auto' }}
										footer={() => (
											<p className={`mb-0 ${!isOption ? 'ms-auto' : ''}`}>
												{t('ACTIONS.PROGRAM_TOTAL_SHARES')} {numberOfSharesWatch} -{' '}
												<span style={{ color: distributedTotal == numberOfSharesWatch ? '#00b96b' : '#ff4d4f' }}>
													{t('COMMON.DISTRIBUTED')} {distributedTotal}
												</span>
											</p>
										)}
									/>
								</div>
							)}

							<Divider />

							<Flex justify="space-between" align="center" className="mt-3">
								<Checkbox onChange={(event) => setAcknowledge(event.target.checked)}>
									<span className="fw-semibold d-block">{t('ACTIONS.LOYALTY_ACKNOWLEDGE1')}</span>
									<span className="text-light">{isPeriodic ? t('ACTIONS.LOYALTY_ACKNOWLEDGE3') : t('ACTIONS.LOYALTY_ACKNOWLEDGE2')}</span>
								</Checkbox>
								<Tooltip title={(isPeriodic && distributedTotal != numberOfSharesWatch) ? t('ACTIONS.DISTRIBUTED_TOTAL_NOT_EQUAL_NUMBER_OF_SHARES') : ''}>
									<Button type="primary" className="px-4" disabled={!acknowledge || (isPeriodic && distributedTotal != numberOfSharesWatch)} htmlType="submit">
										{t('COMMON.SUBMIT')}
									</Button>
								</Tooltip>
							</Flex>
						</Card>
					</Form>

					<VerificationModal
						open={verificationModalOpen}
						title={t('ACTIONS.CONFIRM_GRANT')}
						note={t('ACTIONS.CONFIRM_GRANT_DESC')}
						isLoading={isSubmitting}
						onCancel={() => setVerificationModalOpen(false)}
						onVerificationSuccess={onSubmit}
						module="loyalty_program_create"
					/>
				</>
			)}
		</>
	);
};

export default LoyaltyProgramCreatePage;
